/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { useCharacterLimit } from "components/editor/useCharacterLimit";
import classNames from "components/ui/classNames";
import { useMemo, useState } from "react";

const CHARACTER_LIMIT = 5;
let textEncoderInstance: null | TextEncoder = null;

function textEncoder(): null | TextEncoder {
  if (window.TextEncoder === undefined) {
    return null;
  }

  if (textEncoderInstance === null) {
    textEncoderInstance = new window.TextEncoder();
  }

  return textEncoderInstance;
}

function utf8Length(text: string) {
  const currentTextEncoder = textEncoder();

  if (currentTextEncoder === null) {
    // http://stackoverflow.com/a/5515960/210370
    const m = encodeURIComponent(text).match(/%[89ABab]/g);
    return text.length + (m ? m.length : 0);
  }

  return currentTextEncoder.encode(text).length;
}

export function CharacterLimitPlugin({
  charset = "UTF-16",
  maxLength = CHARACTER_LIMIT,
}: {
  charset: "UTF-8" | "UTF-16";
  maxLength: number;
}): JSX.Element {
  const [editor] = useLexicalComposerContext();

  const [remainingCharacters, setRemainingCharacters] = useState(maxLength);

  const characterLimitProps = useMemo(
    () => ({
      remainingCharacters: setRemainingCharacters,
      strlen: (text: string) => {
        if (charset === "UTF-8") {
          return utf8Length(text);
        }
        if (charset === "UTF-16") {
          return text.length;
        }
        throw new Error("Unrecognized charset");
      },
    }),
    [charset],
  );

  useCharacterLimit(editor, maxLength, characterLimitProps);
  const isCharactersLimitExceeded = remainingCharacters < 0;

  return (
    <div className="flex justify-end">
      <span
        className={classNames("rounded-lg bg-slate-100 p-0.5 px-2 text-sm font-medium text-gray-400", {
          "text-red-500": isCharactersLimitExceeded,
        })}
      >
        {maxLength - remainingCharacters} / {remainingCharacters}
      </span>
    </div>
  );
}
